import { signInWithCustomToken, signInWithEmail, signUpWithEmailAndPassword } from 'library/firebase/fb.auth.utils';
import {
	clearStoredSession,
	forceRefreshToken,
	isAuthenticated,
	isKindeSession,
	logout as providerLogout,
	markSessionExpired
} from 'library/auth/authProvider';
import { readPendingPartnerInvite, redirectToPartnerInviteAccept } from 'library/partnerInvite/pendingInvite';
import { stashOrgLink, takeOrgLink } from 'library/supportAccess/pendingOrg';
import { stashBillingReturnFromLocation } from 'library/billing/billingReturn';
import { billingOnlyProfileFor, isBillingOnlyProfile, isBillingOnlyWorkspace } from 'library/helpers/billingContact';
import AuthHelper from 'library/helpers/authHelper';
import { logUserActivityEvent } from 'library/helpers/Logger';
import SuperFetch from 'library/helpers/superFetch';
import { isAccessRefusal, isProfileLocked } from 'library/helpers/personStatus';
import { getToken } from 'library/helpers/utility';
import { call, delay, put, select, takeLatest } from 'redux-saga/effects';
import { appActions } from 'reduxStore/app/slice';
import { ACCESS_SHELL_DASHBOARDS } from 'reduxStore/app/saga';
import { defaultSettingsActions } from 'reduxStore/defaultSettings/slice';
import { organisationActions } from 'reduxStore/organisations/slice';
import { settingsActions } from 'reduxStore/settings/slice';
import { typesActions } from 'reduxStore/types/slice';
import { featureFlagActions } from 'reduxStore/featureFlags/slice';
import { Notification } from 'zComponents/atoms';
import { env } from 'config/env';
import { setupNewWorkspace } from './helpers';
import { authActions } from './slice';
const selectedProfile = (state) => state.Auth.selectedProfile;

// Routes where a Kinde session legitimately has ZERO orgs — a partner member is keyed by
// (provider, subject), NOT an org Person. On these we must never run the no-workspace logout
// or the /welcome "no matching invitation" redirect; the partner flow owns the page.
const PARTNER_ROUTES = ['/partner-invite/accept', '/partner-portal'];
const onPartnerRoute = () => PARTNER_ROUTES.some((r) => window.location.pathname.startsWith(r));

// The authenticated account belongs to NO workspace. A statement about the ACCOUNT, and the only
// thing the Kinde "no matching invitation" screen (G2) and the Firebase no-workspace toast are
// allowed to answer to. It means "you're signed in but can't enter", as opposed to a transient
// failure, so we surface a clear reason before logging out.
const NO_WORKSPACE_ACCESS_ERRORS = ['No Permissions Found'];

// We could not READ the Person row for a workspace the user demonstrably belongs to —
// `orgByPerson` already returned it moments earlier.
//
// 🛑 THESE TWO USED TO SIT IN THE LIST ABOVE AND THAT WAS THE BUG. A 500/401 on
// `GET /people/:personID` told a fully-provisioned admin "we couldn't find an invitation for this
// email" — measured 2026-09-22 driving the real app: the redirect fires <1s after boot, with one
// valid org in the store. Worse mid-session: `people/saga.js` and CompleteProfileModal re-dispatch
// `getUserProfile` after a profile edit, so one failed refresh threw a working user out of the
// dashboard and onto the G2 screen. A failed READ is a transient condition, never a verdict about
// the account — so it gets a retryable error, never the G2 screen and never a logout.
const PROFILE_UNAVAILABLE_ERRORS = ['Person Profile Not Found.', 'No Person Profile Found'];

// A disabled person keeps the shell but holds no role, so every gate closes like for a role-less user;
// routeRenderer then shows AccountDisabled on every route.
const profileRoles = (person, workspace) => (isProfileLocked(person) ? {} : workspace.roles);

export function* loginRequest({ payload }) {
	// login the user in with email and password
	const { email, password } = payload;

	try {
		const user = yield call(signInWithEmail, { email, password });

		if (user.user) {
			// get user token and set it in the localstorage
			const token = yield user.user.getIdToken();
			localStorage.setItem('id_token', token);

			// Record the door here too: this path never reloads, so checkAuthorization does not run
			// again and sign-out would otherwise have to rediscover the provider over the network.
			yield put(authActions.setProvider('firebase'));
			yield put(authActions.loginSuccess());
		} else {
			throw new Error('User login failed, please try again.');
		}
	} catch (error) {
		yield put(authActions.loginError(error.message));
	}
}

export function* loginSuccess() {
	// Do Actions on Login Success

	// 1: load User's all the workspaces.
	yield put(organisationActions.loadOrganisations());
}

export function* loginError() {
	// Do Actions on Login Error

	yield delay(15000); //Reset the error after 15 seconds

	// Reset the error after 15 seconds
	yield put(authActions.resetError());
}

export function* getUserProfile({ payload: chosenOrgID } = {}) {
	try {
		// Is there an authenticated session (either provider)?
		const authed = yield call(isAuthenticated);

		// Get the workspaces from the store
		const workspaceData = yield select((state) => state.Organisations.data);

		// If the user is not logged in, logout the user
		if (!authed) {
			throw new Error('User not logged in');
		}

		// If the user has no permissions (not part of any org), logout the user — UNLESS this is a
		// partner session. A partner member is keyed by (provider, subject), not an org Person, so
		// zero orgs is EXPECTED for a partner-only user; the normal no-workspace logout (and the
		// Kinde no-invitation redirect) would strand them. This guard runs FIRST, before the throw,
		// so it always wins:
		//  - on a partner route (accept page / partner portal) → let the page drive; keep the session;
		//  - a pending invite elsewhere → hand off to the accept page (no clear here — the accept page
		//    clears the stash on mount; leaving it lets this + checkAuthorization redirect to the SAME
		//    place if they race, instead of one clearing it and the other bouncing to /welcome).
		// Partner-invite hand-off — deliberately BEFORE the org-count branch.
		//
		// 🛑 THIS USED TO LIVE INSIDE `workspaceData.length <= 0` and that was the bug. The flow assumed
		// a partner invitee is brand new to the platform ("a new partner user has no org"), but the
		// realistic invitee is an installer who ALREADY has a Person row — they have been added to a
		// customer site at some point. With one or more orgs the zero-org branch never ran, the stashed
		// token was never read, and Kinde's return dumped them into the normal dashboard bootstrap (org
		// picker → 401) with the invite left unredeemed. Measured on prod 2026-08-06: two of two real
		// invitees hit this, because both already held a Person row.
		//
		// Guarded by !onPartnerRoute() because redirectToPartnerInviteAccept is a full-page replace to
		// the accept page — firing it while already there would reload forever.
		if (!onPartnerRoute()) {
			const pendingPartnerInvite = readPendingPartnerInvite();
			if (pendingPartnerInvite) {
				redirectToPartnerInviteAccept(pendingPartnerInvite);
				return;
			}
		}

		if (workspaceData.length <= 0) {
			if (onPartnerRoute()) {
				// A partner-only user has no org profile to load, so the org bootstrap is DONE — mark it
				// complete so the app shell stops showing the UserLoading screen (which gates on
				// userLoadingComplete) and renders /partner-portal, which redirects to the standalone
				// Partner app. Without this the route spins forever ("Loading is taking longer…").
				yield put(appActions.setUserLoadingComplete());
				return;
			}

			// 🛑 AN EMPTY LIST IS NOT THE SAME AS A LIST THAT NEVER ARRIVED, and the store cannot tell
			// them apart from `data` alone — `[]` is also the initial state. Only `loadOrganisationsSuccess`
			// sets this flag, so until it is true the emptiness means "not loaded yet", never "no orgs".
			//
			// This saga is normally driven by `loadOrganisationsSuccess`, where the precondition holds.
			// The loader's 30s stall-recovery (PublicScreenContainers/Loading + MainDashboardContainers/
			// Loading) dispatches it UNCONDITIONALLY, which broke that assumption: measured 2026-09-22,
			// any `orgByPerson` failure — a 5xx, a dropped connection, or the "Kinde sign-in is not
			// enabled for your organisation yet" refusal — left `data` empty, and at exactly +31s the
			// retry read it as "No Permissions Found" and replaced an accurate on-screen message with
			// the "no matching invitation" screen. Bail out instead: the same retry re-dispatches
			// `loadOrganisations`, and its success re-enters this saga with a real list.
			const organisationsLoaded = yield select((state) => state.Organisations.organisationLoadingComplete);
			if (!organisationsLoaded) {
				yield put(authActions.getUserProfileDeferred());
				return;
			}

			throw new Error('No Permissions Found');
		} else {
			// Get the current user permissions from the store
			const currentSelectedProfile = yield select(selectedProfile);

			// Try to get saved orgID from localStorage
			const savedOrgID = parseInt(localStorage.getItem('od'), 10);

			// Find the current workspace from the user permissions
			const savedWorkspace = savedOrgID && workspaceData.find((org) => org.orgID === savedOrgID);

			// The org the Kinde session signed in to, flagged by the backend (isPreselected). Kinde's
			// hosted selector asks "where do you want to sign in?" and we used to ignore the answer.
			//
			// ⚠️ This is a HINT, not an authorisation. The backend only ever MARKS an org that was
			// already resolved from MSSQL off the verified subject — it cannot add one — so finding it
			// here is a lookup in a list the user demonstrably holds. Do not "simplify" this into
			// trusting an orgID that arrives from the token.
			const preselectedWorkspace = workspaceData.find((org) => org.isPreselected);

			// Priority: an in-session switch beats the login choice (it is more recent intent), the
			// login choice beats a saved localStorage value (which may be weeks stale), and first-in-list
			// is the last resort. Before this, a fresh Kinde login with an org choice still landed on
			// whatever `od` happened to hold.
			// A `?org=` deep link (Partner platform "Open in Access") wins when it names an org in this user's own list;
			// otherwise it's ignored. Taken (and cleared) here, so it's used once.
			const linkedOrgID = takeOrgLink();
			const linkedWorkspace = linkedOrgID && workspaceData.find((org) => org.orgID === linkedOrgID);

			// Picked on the AccessRefused screen; ignored unless it is in the user's own list.
			const chosenWorkspace = chosenOrgID && workspaceData.find((org) => org.orgID === chosenOrgID);

			let workspaceToDispatch;
			if (linkedWorkspace || chosenWorkspace) {
				workspaceToDispatch = linkedWorkspace || chosenWorkspace;
				localStorage.setItem('od', String(workspaceToDispatch.orgID));
			} else if (currentSelectedProfile.orgID && currentSelectedProfile.personID) {
				// Use fresh workspace data that matches current profile's orgID
				workspaceToDispatch =
					workspaceData.find((org) => org.orgID === currentSelectedProfile.orgID) || currentSelectedProfile;
			} else {
				workspaceToDispatch = preselectedWorkspace || savedWorkspace || workspaceData[0];
			}

			const userPermissions = workspaceData.map((workspace) => ({
				personID: workspace.personID,
				orgID: workspace.orgID,
				roles: workspace.roles,
				orgAlias: workspace.alias,
				orgSlug: workspace.orgSlug || null,
				...(isBillingOnlyWorkspace(workspace) ? { billingContactOnly: true } : {})
			}));

			// 🛑 A BILLING-ONLY ORG (D48) NEVER READS THE PERSON ROW. `GET /people/:id` is an operational route the
			// server refuses this person (403), and that refusal would read as "Person Profile Not Found". The
			// bootstrap entry already says who they are in this org; that is all a billing-only session runs on.
			if (isBillingOnlyWorkspace(workspaceToDispatch)) {
				yield put(
					authActions.getUserProfileSuccess({
						selectedProfile: billingOnlyProfileFor(workspaceToDispatch),
						userPermissions
					})
				);
				return;
			}

			// Extract the  person profile from Database for the workspace
			const fetchPersonProfile = () =>
				SuperFetch.get(`/people/${workspaceToDispatch.personID}`, {}, {}, workspaceToDispatch.orgID);

			let res = yield fetchPersonProfile();

			// This is the FIRST org-scoped call of the session, so it races a not-yet-fresh cached
			// token exactly like the `orgByPerson` bootstrap does — organisations/saga.js already
			// carries this guard, and this call sits one step behind it. Force a renewal and retry
			// ONCE; a genuinely invalid token still 401s and falls through to the throw below.
			// Before this, that race surfaced as "Person Profile Not Found." — i.e. the G2 screen.
			if (res?.error?.statusCode === 401 || res?.statusCode === 401) {
				yield call(forceRefreshToken);
				res = yield fetchPersonProfile();
			}

			// Disabled / outside validity in this org: a verdict, not a failed read — retrying never ends.
			if (isAccessRefusal(res?.errorCode)) {
				yield put(authActions.getUserProfileRefused({ code: res.errorCode, orgID: workspaceToDispatch.orgID }));
				return;
			}

			if (!res?.data) {
				throw new Error('Person Profile Not Found.');
			}
			const { data: newPersonProfile } = res;

			if (newPersonProfile.personID) {
				yield put(
					authActions.getUserProfileSuccess({
						selectedProfile: {
							...newPersonProfile,
							roles: profileRoles(newPersonProfile, workspaceToDispatch),
							orgAlias: workspaceToDispatch.alias,
							// Organisation.OrgSlug from the bootstrap payload — the CANONICAL segment for
							// guest URLs. Without it resolveOrgSlug fell back to the alias and every QR
							// link pointed at an org that does not exist.
							orgSlug: workspaceToDispatch.orgSlug || null
						},
						userPermissions
					})
				);
			} else {
				throw new Error('No Person Profile Found');
			}
		}
	} catch (error) {
		const noWorkspaceAccess = NO_WORKSPACE_ACCESS_ERRORS.includes(error.message);

		// The workspace is real; only the profile read failed (see PROFILE_UNAVAILABLE_ERRORS).
		// Neither the G2 screen nor a logout is an honest answer here — one accuses the account of
		// not being invited, the other ends a valid session over one bad response. Surface it and
		// stop: on boot the loader shows this message and its 30s retry re-runs us; mid-session the
		// dashboard stays up on the profile it already has.
		if (PROFILE_UNAVAILABLE_ERRORS.includes(error.message)) {
			yield put(authActions.getUserProfileError("We couldn't load your profile."));
			return;
		}

		// G2 — a signed-in KINDE user whose verified email matched no pre-created Person
		// (wrong-email signup, or not yet invited to any org via the onboarding bridge).
		// Send them to the dedicated `/welcome` "no matching invitation" screen instead of
		// the silent Kinde logout redirect (a full-page redirect to Kinde that would drop
		// any toast). The screen offers an explicit sign-out. Guard against a redirect loop:
		// the app re-bootstraps on that screen and lands back here, so only redirect when we
		// aren't already on /welcome (then just stop — no logout, the screen owns sign-out).
		if (noWorkspaceAccess && (yield call(isKindeSession))) {
			yield put(authActions.getUserProfileError(error.message));
			if (!window.location.pathname.startsWith('/welcome')) {
				window.location.replace('/welcome?error=no-invitation');
			}
			return;
		}

		// Firebase path (unchanged): tell the user why they're bounced back to sign-in
		// instead of doing it silently. logout() resets auth state (wiping `error`), so a
		// toast — which renders at the app root and survives the redirect — is how the
		// message reaches the sign-in page.
		if (noWorkspaceAccess) {
			Notification(
				'warning',
				'No workspace access',
				"Your account isn't set up for any workspace yet. Please contact your administrator."
			);
		}
		yield put(authActions.getUserProfileError(error.message));
		yield put(authActions.logout());
	}
}

// This is called when the user profile is loaded successfully
// This is called when the user switches between the profiles.
export function* getUserProfileSuccess() {
	//* Do Actions once the user profile is loaded

	// A billing-only session (D48) loads nothing org-wide: flags, default settings and types are all refused to it.
	// The app saga's boot listener releases the loader for it; the Billing page loads what it shows.
	if (isBillingOnlyProfile(yield select(selectedProfile))) return;

	// 0: Resolve this org's dev-controlled feature flags. Unconditional (runs on profile
	// load AND org switch) since flags are per-org and must refresh when the org changes.
	yield put(featureFlagActions.loadFeatureFlags());

	// 1: Load Person Workspaces. //? this is done to ensure that the workspaces are loaded properly
	const { organisationLoading, organisationLoadingComplete } = yield select((state) => state.Organisations);

	if (!organisationLoadingComplete && !organisationLoading) {
		yield put(organisationActions.loadOrganisations());
	}

	// 2: Load all the org-scoped default settings & Groups — only once an org is
	// in scope. On first boot the selectedProfile has no orgID yet (it's attached
	// later in switchProfile), so firing these here sent requests to
	// /v5/undefined/… which the backend correctly 404s. This saga re-runs on
	// switchProfileSuccess with a valid orgID, so gating on orgID loses nothing
	// and removes both the boot 404s and the retry race (an error resets the
	// loading flag but not LoadingComplete, so the re-run recovers — unless it
	// lands while the undefined request is still in flight).
	const { orgID } = yield select(selectedProfile);
	if (orgID) {
		const {
			orgDefaultSettingsLoadingComplete,
			personDefaultSettingsLoadingComplete,
			groupSettingsLoadingComplete,

			orgDefaultSettingsLoading,
			personDefaultSettingsLoading,
			groupSettingsLoading
		} = yield select((state) => state.DefaultSettings);

		// Load the default org settings if not loaded
		if (!orgDefaultSettingsLoadingComplete && !orgDefaultSettingsLoading) {
			yield put(defaultSettingsActions.loadOrgDefaultSettings());
		}

		// Load the default person settings if not loaded
		if (!personDefaultSettingsLoadingComplete && !personDefaultSettingsLoading) {
			yield put(defaultSettingsActions.loadPersonDefaultSettings());
		}

		// Load the default Settings group if not loaded
		if (!groupSettingsLoadingComplete && !groupSettingsLoading) {
			yield put(defaultSettingsActions.loadGroupSettings());
		}
	}
	// 3: Org & Person settings are loaded automatically by
	// loadOrgDefaultSettingsSuccess / loadPersonDefaultSettingsSuccess
	// in defaultSettings/saga.js — no need to dispatch them here
	// (doing so caused duplicate fetches).

	// 4 Load all types required for the all the Dashboards.
	yield put(typesActions.loadAllTypes());
}

export function* logout() {
	// Provider-aware logout: clears platform localStorage keys, then either redirects
	// through Kinde's logout endpoint or signs out of Firebase (unchanged path).
	try {
		// 🛑 Hand the provider down rather than making `logout` rediscover it. Discovery cost an
		// `isAuthenticated()` round-trip — roughly a second of "Signing out..." spent asking whether
		// a session was live immediately before ending it.
		const providerID = yield select((state) => state.Auth.providerID);
		const result = yield call(providerLogout, { providerID });
		// 🛑 On the Kinde path the browser is already navigating, so leave `signingOut` set and let
		// the page go. Releasing it here would route to a lazy /signin whose chunk load paints a
		// Suspense loader for a screen we never arrive at — the flicker.
		if (!result?.redirected) yield put(authActions.logoutComplete());
	} catch (error) {
		// Never strand the user on the loader: the store is already signed out, so land them on
		// sign-in even if the provider call failed.
		yield put(authActions.logoutComplete());
	}
}

/**
 * The session ended on its own: mark it, then take the ordinary provider-aware sign-out.
 * 🛑 Marker FIRST — on the Kinde path `logout` hands the page away and nothing after it runs.
 */
export function* sessionExpired() {
	yield call(markSessionExpired);
	yield call(logout);
}

export function* switchProfile({ payload: orgID }) {
	try {
		// Get the workspaces from the store
		const workspaceData = yield select((state) => state.Organisations.data);

		// If the user has no permissions (not part of any org), logout the user
		if (workspaceData.length <= 0) {
			throw new Error('No Permissions Found');
		}

		// Find the current workspace from the user permissions
		const workspace = workspaceData.find((w) => w.orgID === orgID) || {};

		// Billing-only in that org (D48): no person read (refused to this person), no roles.
		if (isBillingOnlyWorkspace(workspace)) {
			localStorage.setItem('od', workspace.orgID);
			yield put(authActions.switchProfileSuccess({ selectedProfile: billingOnlyProfileFor(workspace) }));
			return;
		}

		// Extract the  person profile from Database for the workspace
		const res = yield SuperFetch.get(`/people/${workspace.personID}`, {}, {}, workspace.orgID);
		// Refused in that org: enter it locked (no roles) so the shell shows AccountDisabled.
		const newPersonProfile = isAccessRefusal(res?.errorCode)
			? {
					personID: workspace.personID,
					orgID: workspace.orgID,
					accessRefusal: { code: res.errorCode, orgID: workspace.orgID }
				}
			: res?.data;

		// If the person profile is not found, throw an error
		if (!newPersonProfile?.personID) {
			throw new Error('No Person Profile Found');
		}

		// Set the workspace in the local storage
		localStorage.setItem('od', workspace.orgID);

		yield put(
			authActions.switchProfileSuccess({
				selectedProfile: {
					...newPersonProfile,
					roles: profileRoles(newPersonProfile, workspace),
					orgAlias: workspace.alias,
					orgSlug: workspace.orgSlug || null
				}
			})
		);
	} catch (error) {
		yield put(authActions.switchProfileError(error.message));
	}
}

export function* switchProfileSuccess() {
	// A locked org 403s every load; the shell only shows AccountDisabled there. A billing-only org (D48) is refused
	// everything but billing, so the same holds: no settings, no dashboard data. The Billing page loads its own reads.
	const switchedTo = yield select(selectedProfile);
	if (isProfileLocked(switchedTo) || isBillingOnlyProfile(switchedTo)) return;

	// Reset scope key so onScopeChange re-fetches all scoped data for the new org
	yield put(appActions.setHydratedScopeKey(null));

	// Reload org & person settings for the new org
	yield put(settingsActions.loadOrgSettings());
	yield put(settingsActions.loadPersonSettings());

	// Load all the data resources based on the user's selected Dashboard.
	const selectedDashboard = yield select((state) => state.App?.dashboard?.selected);

	if (ACCESS_SHELL_DASHBOARDS.includes(selectedDashboard)) {
		// Access-shell products (Access/Bookings/Vision) share the same shell/hydration.
		yield put(appActions.loadAccessDashboardData());
	} else if (selectedDashboard === 'lockers') {
		// Load the lockers dashboard data
		yield put(appActions.loadLockersDashboardData());
	}
}

export function* signupUser({ payload }) {
	// Do Actions on Signup
	try {
		const { firstName, lastName, email, password, organisationName, mobile } = payload;

		// step 1: Check if the user exists in the system
		const loggedInUser = yield signInWithEmail({ email: email, password: password })
			.then((authUser) => {
				logUserActivityEvent({
					success: true,
					email: email,
					operationType: 'auth/signup'
				});
				return authUser;
			})
			.catch((e) => {
				return e;
			});

		// step 2: Check if the user exist
		if (loggedInUser.user) {
			// If the user exist, but we need to create an workspace for the user

			// step 2.1: create new workspace
			const res = yield setupNewWorkspace({ firstName, lastName, email, organisationName, mobile });

			if (res) {
				// step 2.2: put the success action
				yield put(authActions.signupUserSuccess({ email, password }));
			}

			// step 2.3: return
			return;
		} else if (loggedInUser.code && loggedInUser.code === 'auth/wrong-password') {
			// Step 3: If the user exist, but password is incorrect

			// Step 3.1: throw an error
			throw new Error('We found your account please enter existing password.');
		} else {
			// step 4: if the user does not exist, create a new user and then create a workspace for the user.
			yield signUpWithEmailAndPassword({ email, password })
				.then((newUser) => newUser)
				.catch((e) => {
					throw new Error(e.message);
				});

			// step 4.1: create new workspace
			const res = yield setupNewWorkspace({ firstName, lastName, email, organisationName, mobile });

			if (res) {
				// step 4.2: put the success action
				yield put(authActions.signupUserSuccess({ email, password }));
			}
		}
	} catch (error) {
		yield put(authActions.signupUserError(error.message));
	}
}

export function* signupUserSuccess({ payload }) {
	// Do Actions
	yield put(authActions.login({ email: payload.email, password: payload.password }));
}

export function* signupUserError() {
	// Do Actions

	yield delay(15000); //Reset the error after 15 seconds

	// Reset the error after 15 seconds
	yield put(authActions.resetError());
}

function* exchangeToken(idToken) {
	const baseUrl =
		process.env.NODE_ENV === 'development'
			? `http://${window.location.hostname}:${env.localApiPort}`
			: 'https://df.api.zezamii.com';

	const res = yield call(fetch, `${baseUrl}/v4/auth/exchange-token`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ idToken })
	});

	if (!res.ok) {
		throw new Error('Token exchange failed');
	}

	const { data } = yield call([res, res.json]);
	const userCredential = yield call(signInWithCustomToken, data.customToken);
	const newToken = yield call([userCredential.user, userCredential.user.getIdToken]);
	localStorage.setItem('id_token', newToken);
}

/**
 * Checks the authorization by listening to the CHECK_AUTHORIZATION action and validating the token.
 * If the token is valid and the user is logged in, it dispatches the loginSuccess action with the token.
 */
export function* checkAuthorization() {
	// Strip and keep a `?org=` deep link before anything routes or redirects to sign-in (getUserProfile uses it).
	stashOrgLink();
	// A Billing link (`/access/billing?orgID=`) is remembered the same way, so sign-in comes back to it (D48).
	stashBillingReturnFromLocation();
	try {
		// Check for cross-origin token exchange (e.g. from Rooms Dashboard). This consumes a
		// `?token=` param as a Firebase custom token. EXCLUDE routes that own `?token=` for their
		// own purpose — notably the partner-invite accept page (`/partner-invite/accept?token=` is
		// the invite token, NOT an SSO token); otherwise this deletes the param + posts the invite
		// token to /v4/auth/exchange-token (401) and the accept page sees no token → "invalid link".
		const url = new URL(window.location.href);
		const incomingToken = url.searchParams.get('token');
		const ownsTokenParam = window.location.pathname.startsWith('/partner-invite/accept');

		if (incomingToken && !ownsTokenParam) {
			url.searchParams.delete('token');
			window.history.replaceState({}, '', url.pathname + url.search);
			yield call(exchangeToken, incomingToken);
			// A Firebase custom token, so this is a Firebase session.
			yield put(authActions.setProvider('firebase'));
			yield put(authActions.loginSuccess());
			return;
		}
	} catch (error) {
		// Token exchange failed — fall through to normal auth check
	}

	// Restore a live Kinde session. isKindeSession awaits the client singleton, which
	// both processes a /callback ?code exchange AND restores a persisted session on
	// refresh — so this one branch covers post-login and reload. It returns false when
	// the flag is off or the session is Firebase, so the unchanged localStorage/expiry
	// check below still runs for those.
	const kindeSession = yield call(isKindeSession);
	if (kindeSession) {
		// Remember the door. Sign-out then needs no SDK probe to know which one to close.
		yield put(authActions.setProvider('kinde'));
		yield put(authActions.loginSuccess());
		// Partner-invite hand-off (early, UX): a new partner user has no org, so send them to the
		// accept page BEFORE loadOrganisations→getUserProfile can start a zero-org logout. The
		// full-page replace aborts that chain. Skip when already on a partner route (no loop). Do
		// NOT clear here — the getUserProfile guard redirects to the SAME place if it races ahead,
		// and the accept page clears the stash on mount. Session is already live (loginSuccess
		// above), so the accept page can accept immediately.
		const pendingPartnerInvite = readPendingPartnerInvite();
		if (pendingPartnerInvite && !onPartnerRoute()) {
			redirectToPartnerInviteAccept(pendingPartnerInvite);
		}
		return;
	}

	const token = getToken().get('idToken');
	const loggedIn = new AuthHelper().checkExpirity(token);

	// A stale id_token can linger in localStorage after the Firebase session itself is
	// gone (e.g. a prior Kinde test, or cleared IndexedDB). Confirm a live session
	// actually backs the token before restoring — otherwise we'd "log in" and then fail
	// the bootstrap with "No FBPersonID". isAuthenticated is provider-aware; the Kinde
	// branch already returned above, so here it resolves the Firebase session.
	const hasSession = yield call(isAuthenticated);

	if (token && loggedIn && hasSession) {
		yield put(authActions.setProvider('firebase'));
		yield put(authActions.loginSuccess(token));
	} else {
		// 🛑 NOT `logout`. There is no session to end here — this is a cold boot that found none, and
		// `logout` would run the provider sign-out again and, since it raises `signingOut`, paint a
		// "Signing out..." loader on every anonymous page load. The stale token still has to go,
		// which is the half of `logout` this branch actually needed.
		yield call(clearStoredSession);
		yield put(authActions.logoutComplete());
	}
}
// The main export for the saga
export default function* authSaga() {
	yield takeLatest(authActions.checkAuthorization, checkAuthorization);
	yield takeLatest(authActions.login, loginRequest);
	yield takeLatest(authActions.loginSuccess, loginSuccess);
	yield takeLatest(authActions.loginError, loginError);
	yield takeLatest(authActions.getUserProfile, getUserProfile);
	yield takeLatest([authActions.getUserProfileSuccess, authActions.switchProfileSuccess], getUserProfileSuccess);
	yield takeLatest(authActions.switchProfileSuccess, switchProfileSuccess);
	yield takeLatest(authActions.logout, logout);
	yield takeLatest(authActions.sessionExpired, sessionExpired);
	yield takeLatest(authActions.switchProfile, switchProfile);
	yield takeLatest(authActions.signupUser, signupUser);
	yield takeLatest(authActions.signupUserSuccess, signupUserSuccess);
	yield takeLatest(authActions.signupUserError, signupUserError);
}
